import { useEffect, useState } from "react";
import { authFetch } from "../lib/api";

export const PrecinctCaptainsPanel = ({ facility }) => {
  const fid = facility.facility_id;
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // { geoid, name: precinctName }
  const [form, setForm] = useState({
    name: "",
    title: "",
    phone: "",
    email: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (text, err = false) => {
    setMsg({ text, err });
    setTimeout(() => setMsg(null), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await authFetch(`/rag/precinct-captains?facility_id=${fid}`);
      setCaptains(data);
    } catch {
      flash("Failed to load captains", true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // runs once on mount

  const handleSearch = async () => {
    if (search.trim().length < 2) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const data = await authFetch(
        `/fieldiq/precincts?facility_id=${fid}&q=${encodeURIComponent(search.trim())}`,
      );
      setSearchResults(data);
    } catch {
      flash("Precinct search failed", true);
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = (p) => {
    setSelected({
      geoid: p.geoid,
      name: p.name || `Precinct ${p.precinct_id}`,
    });
    setSearchResults([]);
    setSearch("");
    const existing = captains.find((c) => c.geoid === p.geoid);
    setForm({
      name: existing?.name || "",
      title: existing?.title || "",
      phone: existing?.phone || "",
      email: existing?.email || "",
    });
  };

  const handleSave = async () => {
    if (!selected || !form.name.trim()) return;
    setSaving(true);
    try {
      await authFetch("/rag/precinct-captains", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          geoid: selected.geoid,
          ...form,
        }),
      });
      flash("Saved");
      setSelected(null);
      setForm({ name: "", title: "", phone: "", email: "" });
      await load();
    } catch {
      flash("Save failed", true);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (geoid) => {
    if (!window.confirm("Remove this precinct captain?")) return;
    try {
      await authFetch(`/rag/precinct-captains/${geoid}?facility_id=${fid}`, {
        method: "DELETE",
      });
      flash("Removed");
      await load();
    } catch {
      flash("Delete failed", true);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      {/* Search + form */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700">
          Add / Edit Precinct Captain
        </h3>

        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search precinct by name or number…"
            className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            disabled={searching || search.trim().length < 2}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {searching ? "…" : "Search"}
          </button>
        </div>

        {searchResults.length > 0 && (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
            {searchResults.map((p) => (
              <button
                key={p.geoid}
                onClick={() => handleSelect(p)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors"
              >
                <span className="font-medium text-slate-800">
                  {p.name || `Precinct ${p.precinct_id}`}
                </span>
                <span className="ml-2 text-slate-400 text-xs">
                  #{p.precinct_id}
                </span>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="space-y-3 pt-2">
            <p className="text-xs text-slate-500">
              Captain for:{" "}
              <span className="font-semibold text-slate-700">
                {selected.name}
              </span>
            </p>
            {[
              {
                k: "name",
                label: "Name",
                placeholder: "Jane Smith",
                required: true,
              },
              { k: "title", label: "Title", placeholder: "Precinct Captain" },
              { k: "phone", label: "Phone", placeholder: "602-555-0100" },
              { k: "email", label: "Email", placeholder: "jane@example.com" },
            ].map(({ k, label, placeholder, required }) => (
              <div key={k}>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {label}
                  {required && " *"}
                </label>
                <input
                  type="text"
                  value={form[k]}
                  onChange={set(k)}
                  placeholder={placeholder}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => {
                  setSelected(null);
                  setForm({ name: "", title: "", phone: "", email: "" });
                }}
                className="text-sm text-slate-400 hover:text-slate-600"
              >
                Cancel
              </button>
              {msg && (
                <span
                  className={`text-xs font-medium ${msg.err ? "text-red-600" : "text-emerald-600"}`}
                >
                  {msg.text}
                </span>
              )}
            </div>
          </div>
        )}

        {!selected && msg && (
          <span
            className={`text-xs font-medium ${msg.err ? "text-red-600" : "text-emerald-600"}`}
          >
            {msg.text}
          </span>
        )}
      </div>

      {/* Current captains list */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">
          Precinct Captains ({captains.length})
        </h3>
        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : captains.length === 0 ? (
          <p className="text-sm text-slate-400 italic">
            No captains assigned yet.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {captains.map((c) => (
              <div
                key={c.geoid}
                className="py-3 flex items-start justify-between gap-4"
              >
                <div>
                  <p className="text-xs text-slate-400 font-medium">
                    {c.precinct_name}
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    {c.name}
                  </p>
                  {c.title && (
                    <p className="text-xs text-slate-500">{c.title}</p>
                  )}
                  <div className="flex gap-3 mt-1">
                    {c.phone && (
                      <span className="text-xs text-slate-500">{c.phone}</span>
                    )}
                    {c.email && (
                      <span className="text-xs text-slate-500">{c.email}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() =>
                      handleSelect({
                        geoid: c.geoid,
                        precinct_id: c.precinct_id,
                        name: c.precinct_name,
                      })
                    }
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(c.geoid)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
